import { Button, Modal, Input, Popconfirm, Tag, Select, Tooltip, message } from "antd";
import {
  SaveOutlined,
  CopyOutlined,
  DeleteOutlined,
  ClearOutlined,
  ThunderboltOutlined,
  ArrowLeftOutlined,
  HistoryOutlined,
  AudioOutlined,
  AudioMutedOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import HistoryDrawer from "./HistoryDrawer";
import useSpeechToText from "../../../hooks/useSpeechToText";

/**
 * Top action bar for the Template Builder.
 *
 * Templates are a single shared library used by all clinicians, and the builder
 * is admin-only, surfaced by the "Admin only / shared" tag. "Generate from
 * description" calls the backend scaffold endpoint and loads the returned draft
 * into the builder for the admin to refine and save.
 */
const SPECIALTY_OPTIONS = [
  { value: "behavioral_health", label: "Behavioral Health" },
  { value: "weight_loss", label: "Weight Loss" },
];

export default function ActionBar({
  template,
  name,
  onNameChange,
  specialtyTag,
  onSpecialtyChange,
  pendingChanges,
  saving,
  onSave,
  onSaveAsNew,
  onClone,
  onArchive,
  onGenerate,
  generating,
  onClearDraft,
  sectionCount,
  onRestore,
}) {
  const navigate = useNavigate();
  const [genOpen, setGenOpen] = useState(false);
  const [genText, setGenText] = useState("");
  const [histOpen, setHistOpen] = useState(false);
  const stt = useSpeechToText();

  const appendDictation = (text) => {
    if (!text) return;
    setGenText((prev) => (prev ? prev.trimEnd() + " " + text : text));
  };

  const toggleMic = () => {
    if (stt.listening) stt.stop();
    else stt.start(appendDictation);
  };

  const goBack = () => {
    if (pendingChanges) {
      Modal.confirm({
        title: "Leave the builder?",
        content: "You have unsaved changes. Leave without saving?",
        okText: "Leave",
        cancelText: "Stay",
        onOk: () => navigate("/admin/templates"),
      });
    } else {
      navigate("/admin/templates");
    }
  };

  const runGenerate = async () => {
    if (!genText.trim()) {
      message.warning("Describe the template you want first.");
      return;
    }
    try {
      await onGenerate?.(genText.trim());
      stt.stop();
      setGenOpen(false);
      setGenText("");
      message.success("Draft generated. Refine the sections, then Save.");
    } catch (err) {
      message.error(
        err?.response?.data?.detail || err?.message || "Could not generate a draft."
      );
    }
  };

  return (
    <div className="h-12 px-4 border-b border-gray-200 bg-white flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Button
          size="small"
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={goBack}
        >
          Back
        </Button>
        <Input
          size="small"
          value={name ?? ""}
          onChange={(e) => onNameChange?.(e.target.value)}
          placeholder="Template name"
          style={{ width: 240 }}
        />
        <Tooltip title={template?.id ? "Specialty cannot be changed on a saved template" : "Choose the clinical specialty for this template"}>
          <Select
            size="small"
            value={specialtyTag ?? "behavioral_health"}
            onChange={onSpecialtyChange}
            options={SPECIALTY_OPTIONS}
            style={{ width: 160 }}
            disabled={!!template?.id}
          />
        </Tooltip>
        <Tag color="blue">Admin only · shared</Tag>
        {pendingChanges && (
          <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
            Unsaved changes
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="primary"
          size="small"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={!pendingChanges}
          onClick={onSave}
        >
          Save
        </Button>

        <Button size="small" onClick={onSaveAsNew} disabled={!pendingChanges}>
          Save as new
        </Button>

        <Button size="small" icon={<CopyOutlined />} onClick={onClone} disabled={!template?.id}>
          Clone
        </Button>

        <Button size="small" danger icon={<DeleteOutlined />} onClick={onArchive} disabled={!template?.id}>
          Archive
        </Button>

        <Button
          size="small"
          icon={<HistoryOutlined />}
          onClick={() => setHistOpen(true)}
          disabled={!template?.id}
        >
          History
        </Button>

        <Popconfirm
          title="Clear all sections? This cannot be undone."
          okText="Clear"
          cancelText="Cancel"
          onConfirm={onClearDraft}
          disabled={!sectionCount}
          placement="bottomRight"
        >
          <Button
            size="small"
            icon={<ClearOutlined />}
            disabled={!sectionCount}
          >
            Clear draft
          </Button>
        </Popconfirm>

        <Tooltip title="Describe the template in plain language and AI drafts a starter you refine. Type it or dictate it with your microphone.">
          <Button
            size="small"
            icon={<ThunderboltOutlined />}
            onClick={() => setGenOpen(true)}
            aria-label="Generate from description"
          >
            Generate from description
          </Button>
        </Tooltip>
      </div>

      <Modal
        title="Generate template from description"
        open={genOpen}
        onCancel={() => {
          stt.stop();
          setGenOpen(false);
        }}
        okText="Generate"
        confirmLoading={generating}
        onOk={runGenerate}
      >
        <p className="mb-3">
          Describe your template in plain language and we will draft the matching
          sections. Type it, or use the microphone to dictate. Refine, then Save.
        </p>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <Input.TextArea
            rows={4}
            value={genText}
            onChange={(e) => setGenText(e.target.value)}
            placeholder="e.g. A weight-loss follow-up template that captures BMI, GLP-1 dose, side effects, and treatment plan progress."
            style={{ flex: 1 }}
          />
          <Tooltip
            title={
              stt.supported
                ? stt.listening
                  ? "Stop dictation"
                  : "Dictate with your microphone"
                : "Voice input needs Chrome, Edge, or Safari"
            }
          >
            <Button
              shape="circle"
              type={stt.listening ? "primary" : "default"}
              danger={stt.listening}
              icon={stt.listening ? <AudioMutedOutlined /> : <AudioOutlined />}
              disabled={!stt.supported}
              onClick={toggleMic}
              aria-label={
                stt.listening ? "Stop dictation" : "Dictate template description"
              }
            />
          </Tooltip>
        </div>
        {stt.listening && (
          <p className="mt-2 text-xs" style={{ color: "#1677ff" }}>
            Listening… {stt.interim && <em>{stt.interim}</em>}
          </p>
        )}
        {stt.error === "not-allowed" && (
          <p className="mt-2 text-xs" style={{ color: "#cf1322" }}>
            Microphone permission is blocked. Enable it in your browser to dictate.
          </p>
        )}
      </Modal>

      <HistoryDrawer
        open={histOpen}
        onClose={() => setHistOpen(false)}
        templateId={template?.id}
        onRestore={onRestore}
      />
    </div>
  );
}
